import { useEffect, useState } from "react";
import { useDogs } from "@/context/DogsContext";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SectionTitle } from "@/components/app/Bits";
import EmptyDogState from "@/components/app/EmptyDogState";
import { ThumbsUp, AlertTriangle, User, Dog } from "lucide-react";
import { toast } from "sonner";

const FIELDS = [
  { key: "owner_strengths", label: "Your strengths", icon: ThumbsUp, owner: "you", placeholder: "What do you bring to this partnership? Patience, consistency, observation skills, fitness, network…" },
  { key: "owner_weaknesses", label: "Your weaknesses", icon: AlertTriangle, owner: "you", placeholder: "Be honest. Where do you struggle? Confidence, follow-through, handling skill, time, etc." },
  { key: "dog_strengths", label: "Your dog's strengths", icon: ThumbsUp, owner: "dog", placeholder: "Drive, structure, biddability, coat, attitude in the ring, recovery, etc." },
  { key: "dog_weaknesses", label: "Your dog's weaknesses", icon: AlertTriangle, owner: "dog", placeholder: "Sensitivity, fault to manage, distractibility, fitness gap, grooming hot spots…" },
];

export default function AssessmentPage() {
  const { activeDog } = useDogs();
  const { user } = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!activeDog) { setForm(null); return; }
    api.get("/assessment", { params: { dog_id: activeDog.id } }).then(({ data }) => {
      setForm({
        owner_strengths: data.owner_strengths || "",
        owner_weaknesses: data.owner_weaknesses || "",
        dog_strengths: data.dog_strengths || "",
        dog_weaknesses: data.dog_weaknesses || "",
      });
    });
  }, [activeDog]);

  if (!activeDog) return <EmptyDogState>{null}</EmptyDogState>;
  if (!form) return null;

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/assessment", { dog_id: activeDog.id, ...form });
      toast.success("Assessment saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="assessment-page">
      <SectionTitle
        overline="Honest look · yearly or twice-yearly"
        title="Assessment"
        action={
          <Button onClick={save} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="save-assessment-button">
            {saving ? "Saving…" : "Save assessment"}
          </Button>
        }
      />

      <p className="text-sm text-muted-foreground max-w-2xl -mt-2">
        Capture what {user?.name || "you"} and <strong>{activeDog.name}</strong> bring to the partnership — and where you each have room to grow. Revisit and revise as you learn.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        {FIELDS.map((f) => {
          const Icon = f.icon;
          const OwnerIcon = f.owner === "dog" ? Dog : User;
          const accent = f.label.toLowerCase().includes("weak");
          return (
            <Card
              key={f.key}
              className={`border-border shadow-none rounded-lg ${accent ? "" : ""}`}
              data-testid={`assessment-${f.key}`}
            >
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <div className={`h-9 w-9 rounded-md flex items-center justify-center ${accent ? "bg-accent text-accent-foreground" : "bg-secondary text-secondary-foreground"}`}>
                    <Icon size={16} />
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                      <OwnerIcon size={11} />
                      <span>{f.owner === "dog" ? activeDog.name : "Handler"}</span>
                    </div>
                    <Label className="font-display text-base font-bold tracking-tight">{f.label}</Label>
                  </div>
                </div>
                <Textarea
                  rows={7}
                  value={form[f.key]}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  placeholder={f.placeholder}
                  className="text-sm leading-6"
                  data-testid={`assessment-input-${f.key}`}
                />
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
